/**
 * SAVENE — Collection filters + sort (AJAX)
 * - Intercepta submit do form de filtros, cliques em tags e sort
 * - Fetch ?section_id=... e substitui #sv-col-results
 * - Atualiza URL via history.pushState
 * - Empty/error states preservados via markup retornado
 */
(function () {
  'use strict';

  const SECTION_ID = 'sv-col-results';
  let currentController = null;

  function getResults() {
    return document.getElementById(SECTION_ID);
  }

  function getSectionName() {
    const el = getResults();
    return el ? el.dataset.sectionId : null;
  }

  function setLoading(loading) {
    const main = document.querySelector('.sv-col-main');
    if (!main) return;
    if (loading) {
      main.classList.add('is-loading');
      main.setAttribute('aria-busy', 'true');
    } else {
      main.classList.remove('is-loading');
      main.removeAttribute('aria-busy');
    }
  }

  async function renderFromUrl(url) {
    const sectionName = getSectionName();
    if (!sectionName) {
      // Fallback: full navigation
      window.location.href = url;
      return;
    }

    if (currentController) currentController.abort();
    currentController = new AbortController();

    setLoading(true);

    try {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionName);

      const res = await fetch(fetchUrl.toString(), {
        signal: currentController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById(SECTION_ID);

      if (!fresh) {
        // Section didn't return the wrapper — fallback to full nav
        window.location.href = url;
        return;
      }

      const current = getResults();
      current.innerHTML = fresh.innerHTML;

      // Update URL (sem o section_id param)
      const cleanUrl = new URL(url, window.location.origin);
      cleanUrl.searchParams.delete('section_id');
      window.history.pushState({ svCollection: true }, '', cleanUrl.pathname + cleanUrl.search);

      // Re-init product card interactions no grid atualizado
      if (typeof window.svInitProductCards === 'function') {
        window.svInitProductCards();
      }

      // Re-bind handlers no wrapper atualizado
      bindAll();

      // Scroll para topo da lista (opcional: só no sort; em filter mantém posição)
      // Decisão: manter posição para não desorientar
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[SV Collection] fetch failed:', err);
      // Fallback: navegação completa
      window.location.href = url;
    } finally {
      setLoading(false);
    }
  }

  // ============================================================
  // HANDLERS
  // ============================================================

  function handleFormChange() {
    const form = document.querySelector('[data-sv-filters]');
    if (!form) return;

    let priceTimer;
    form.addEventListener('input', (e) => {
      const target = e.target;
      // Price manual input: debounce 500ms
      if (target.matches('[data-sv-price-input]')) {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(() => submitForm(form), 500);
        return;
      }
    });

    form.addEventListener('change', (e) => {
      const target = e.target;
      // Checkbox filters: apply imediatamente
      if (target.matches('[data-sv-filter-input]')) {
        submitForm(form);
        return;
      }
    });

    // Prevent native submit
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      submitForm(form);
    });
  }

  function submitForm(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (value === '' || value === null) continue;
      params.append(key, value);
    }

    // Preserve sort_by se estiver na URL atual
    const currentParams = new URLSearchParams(window.location.search);
    const sortBy = currentParams.get('sort_by');
    if (sortBy && !params.has('sort_by')) {
      params.set('sort_by', sortBy);
    }

    const url = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    renderFromUrl(url);
  }

  function handleTagClicks() {
    // Tags removíveis (toolbar + filter body)
    document.querySelectorAll('[data-sv-tag]').forEach((tag) => {
      tag.addEventListener('click', (e) => {
        e.preventDefault();
        renderFromUrl(tag.href);
      });
    });

    // Clear button
    const clear = document.querySelector('[data-sv-clear]');
    if (clear) {
      clear.addEventListener('click', (e) => {
        e.preventDefault();
        renderFromUrl(clear.href);
      });
    }

    // Price chips
    document.querySelectorAll('[data-sv-price-chip]').forEach((chip) => {
      chip.addEventListener('click', (e) => {
        e.preventDefault();
        renderFromUrl(chip.href);
      });
    });
  }

  function handleSortChange() {
    const sort = document.querySelector('[data-sv-sort]');
    if (!sort) return;
    sort.addEventListener('change', (e) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page'); // Reset page
      renderFromUrl(url.pathname + url.search);
    });
  }

  function handleMobileDrawer() {
    const filtersEl = document.querySelector('[data-sv-filters]');
    const openBtn = document.querySelector('[data-sv-filters-open]');
    const closeBtns = document.querySelectorAll('[data-sv-filters-close]');
    let overlay = document.querySelector('.sv-f-overlay');

    if (!filtersEl || !openBtn) return;

    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'sv-f-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      document.body.appendChild(overlay);
    }

    const open = () => {
      filtersEl.classList.add('is-open');
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      filtersEl.classList.remove('is-open');
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    openBtn.addEventListener('click', open);
    closeBtns.forEach((b) => b.addEventListener('click', close));
    overlay.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && filtersEl.classList.contains('is-open')) close();
    });
  }

  // Browser back/forward button
  window.addEventListener('popstate', (e) => {
    if (e.state && e.state.svCollection) {
      renderFromUrl(window.location.pathname + window.location.search);
    }
  });

  function bindAll() {
    handleFormChange();
    handleTagClicks();
    handleSortChange();
    handleMobileDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }
})();
